import { TicketStatus } from "@jugl-web/rest-api/tickets/models/common-types";
import { useTranslations } from "@jugl-web/utils";
import { ReactNode, useCallback, useMemo } from "react";
import { ReactComponent as InProgressIcon } from "./assets/in-progress.svg";
import { ReactComponent as IssuedIcon } from "./assets/issued.svg";
import { ReactComponent as OnCustomerIcon } from "./assets/on-customer.svg";
import { ReactComponent as OnHoldIcon } from "./assets/on-hold.svg";
import { ReactComponent as ResolvedIcon } from "./assets/resolved.svg";

export interface TicketStatusDetails {
  id: TicketStatus;
  label: string;
  icon: ReactNode;
}

export const TICKET_STATUS_IDS: TicketStatus[] = [
  "in_progress",
  "on_customer",
  "on_hold",
  "issued",
  "resolved",
];

export const useTicketStatuses = () => {
  const { t } = useTranslations();

  const ticketStatusDetailsById = useMemo<
    Record<TicketStatus, TicketStatusDetails>
  >(
    () => ({
      in_progress: {
        id: "in_progress",
        label: t({
          id: "client-support-page.inbox-sidebar.in-progress",
          defaultMessage: "In Progress",
        }),
        icon: <InProgressIcon className="shrink-0" />,
      },
      on_customer: {
        id: "on_customer",
        label: t({
          id: "client-support-page.inbox-sidebar.on-customer",
          defaultMessage: "On Customer",
        }),
        icon: <OnCustomerIcon className="shrink-0" />,
      },
      on_hold: {
        id: "on_hold",
        label: t({
          id: "client-support-page.inbox-sidebar.on-hold",
          defaultMessage: "On Hold",
        }),
        icon: <OnHoldIcon className="shrink-0" />,
      },
      issued: {
        id: "issued",
        label: t({
          id: "client-support-page.inbox-sidebar.issued",
          defaultMessage: "Issued",
        }),
        icon: <IssuedIcon className="shrink-0" />,
      },
      resolved: {
        id: "resolved",
        label: t({
          id: "client-support-page.inbox-sidebar.resolved",
          defaultMessage: "Resolved",
        }),
        icon: <ResolvedIcon className="shrink-0" />,
      },
    }),
    [t]
  );

  const statuses = useMemo<TicketStatusDetails[]>(
    () => TICKET_STATUS_IDS.map((id) => ticketStatusDetailsById[id]),
    [ticketStatusDetailsById]
  );

  const getStatusById = useCallback(
    (id: TicketStatus): TicketStatusDetails =>
      ticketStatusDetailsById[id] ?? ticketStatusDetailsById.in_progress,
    [ticketStatusDetailsById]
  );

  return {
    statuses,
    getStatusById,
  };
};
